import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
} from "services/constants/designTokens";
import {
  sharedTableBodyCellSx,
  sharedTableHeadCellSx,
} from "components/DesignSystem/sharedUiPatterns";

// Shared list-view plumbing for the Monitoring lists (Devices, Event Trace),
// so both use the same standard toolbar/table chrome as the Administration
// lists (CustomFilter + "Default View" saved views + sticky header/footer).

// CustomFilter's height isn't fixed (its chip row only appears once a
// filter/sort is applied) and neither is the footer's, so both are measured.
// Same pattern as Administration > Roles.
export const useMeasuredHeight = () => {
  const [node, setNode] = useState(null);
  const [height, setHeight] = useState(0);
  const ref = useCallback((element) => setNode(element), []);

  useEffect(() => {
    if (!node) return undefined;
    const update = () => setHeight(node.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [ref, height];
};

// Saved views and saved filters are stored per backend submodule
// (`filter-preference`), looked up in the current product's provisioning
// payload. Monitoring and System Configuration provision each screen as a
// module holding one submodule of the same name, so `moduleName` defaults
// to the submodule name; Administration > Organisation passes its own
// module. Without a match the shared hooks skip every filter-preference
// call and saving a view is switched off (canSaveViews).
export const useMonitoringListAccess = (
  subModuleName,
  moduleName = subModuleName,
) => {
  const { domain, userDetails, userScreenAccess, topbarCompanyName } = useSelector(
    (state) => state.tokenReducer,
  );
  const subModuleId = userScreenAccess?.module_details
    ?.find((module) => module.module_name === moduleName)
    ?.submodules?.find(
      (subModule) => subModule.sub_module_name === subModuleName,
    )?.sub_module_id;

  return {
    domain,
    topbarCompanyName,
    userDetails,
    subModuleId,
    canSaveViews: Boolean(subModuleId),
  };
};

// Column widths for a fixed-layout list whose columns can be hidden from the
// toolbar. `widths` is the designed layout with every column showing: a
// percentage per column, a pixel number for a fixed column (an Action
// button), and nothing for the column that takes the remainder. When columns
// are hidden, the visible ones keep their relative proportions and are
// scaled up to fill the table, instead of one column absorbing all the freed
// space. With every column visible the result equals `widths`.
const FIXED_COLUMN_SHARE = 12;
const balancedWidthsCache = new WeakMap();

const balancedColumnWidths = (visibleHeaders, allHeaders, widths) => {
  const keys = [...new Set([...allHeaders, ...visibleHeaders].map((h) => h.key))];
  const isFixed = (key) => typeof widths[key] === "number";
  const flexKeys = keys.filter((key) => widths[key] === undefined);
  const designed = keys.reduce(
    (sum, key) =>
      sum + (isFixed(key) ? FIXED_COLUMN_SHARE : parseFloat(widths[key]) || 0),
    0,
  );
  const flexShare = flexKeys.length
    ? Math.max(100 - designed, 10) / flexKeys.length
    : 0;
  const shareOf = (key) =>
    isFixed(key)
      ? FIXED_COLUMN_SHARE
      : widths[key] === undefined
        ? flexShare
        : parseFloat(widths[key]);

  const visibleKeys = visibleHeaders.map((h) => h.key);
  const total = visibleKeys.reduce((sum, key) => sum + shareOf(key), 0) || 1;
  // One column is left without a width so it absorbs rounding and the
  // fixed-pixel column's difference: the remainder column when it is
  // showing, otherwise the widest visible one.
  const scalable = visibleKeys.filter((key) => !isFixed(key));
  const absorber =
    scalable.find((key) => widths[key] === undefined) ??
    [...scalable].sort((a, b) => shareOf(b) - shareOf(a))[0];

  return Object.fromEntries(
    visibleKeys.map((key) => [
      key,
      isFixed(key)
        ? widths[key]
        : key === absorber
          ? undefined
          : `${((shareOf(key) / total) * 100).toFixed(2)}%`,
    ]),
  );
};

// Width of one column; computed once per visible-column set.
export const balancedColumnWidth = (key, visibleHeaders, allHeaders, widths) => {
  let result = balancedWidthsCache.get(visibleHeaders);
  if (!result) {
    result = balancedColumnWidths(visibleHeaders, allHeaders, widths);
    balancedWidthsCache.set(visibleHeaders, result);
  }
  return result[key];
};

// The table header sticks below the sticky CustomFilter bar on desktop; the
// bar itself is static below `md` (sharedStickyCustomFilterBarSx).
export const monitoringHeadCellSx = (customFilterHeight) => ({
  ...sharedTableHeadCellSx,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  top: { xs: APP_BAR_OFFSET, md: APP_BAR_OFFSET + customFilterHeight },
});

// Same cell padding and text as the Team Workspace Approve lists.
export const monitoringBodyCellSx = { ...sharedTableBodyCellSx };

// Row hover and the selected-row look (slate fill + left bar) used by the
// Team Workspace Approve lists while a row's side panel is open.
export const monitoringRowSx = (selected) => ({
  cursor: "pointer",
  ...designShell.tableRowHover,
  ...(selected ? designShell.neutralSelectedRow : {}),
  "&:last-child td": { borderBottom: "none" },
});

// The row's main name: medium weight, bold while its panel is open.
export const monitoringNameSx = (selected) => ({
  fontSize: 14,
  fontWeight: selected ? 700 : 500,
  color: selected ? designColors.slate900 : "inherit",
  minWidth: 0,
});
